import { useConfirmPasswordReset } from '@amarantos/api-client'
import { createFileRoute, Link } from '@tanstack/react-router'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/button'
import { AuthShell } from '@/features/auth/components/auth-shell'
import { ResetPasswordForm } from '@/features/auth/components/reset-password-form'
import { toConfirmPasswordResetPayload } from '@/features/auth/types/reset-password-form'
import { resetPasswordSearchSchema } from '@/features/auth/utils/reset-password-search'

export const Route = createFileRoute('/reset-password')({
	validateSearch: resetPasswordSearchSchema,
	component: ResetPasswordRoute,
})

function ResetPasswordRoute() {
	const { t } = useTranslation()
	const { token } = Route.useSearch()
	const [tokenRejected, setTokenRejected] = useState(false)
	const [succeeded, setSucceeded] = useState(false)

	const confirmMutation = useConfirmPasswordReset({ mutation: { onSuccess: () => setSucceeded(true) } })

	if (token.trim() === '' || tokenRejected) {
		return (
			<AuthShell
				title={t('auth.reset_password.invalid_link.title')}
				description={t('auth.reset_password.invalid_link.description')}
			>
				<Button asChild className='w-full'>
					<Link to='/forgot-password'>{t('auth.reset_password.invalid_link.request_new')}</Link>
				</Button>
			</AuthShell>
		)
	}

	if (succeeded) {
		// The endpoint returns no session, so this is an inline success screen, not an auto-login.
		return (
			<AuthShell
				title={t('auth.reset_password.success.title')}
				description={t('auth.reset_password.success.description')}
			>
				<Button asChild className='w-full'>
					<Link to='/login'>{t('auth.reset_password.success.continue')}</Link>
				</Button>
			</AuthShell>
		)
	}

	return (
		<AuthShell title={t('auth.reset_password.title')} description={t('auth.reset_password.description')}>
			<ResetPasswordForm
				isSubmitting={confirmMutation.isPending}
				onSubmit={async (values) => {
					await confirmMutation.mutateAsync({ data: toConfirmPasswordResetPayload(token, values) })
				}}
				onInvalidToken={() => setTokenRejected(true)}
			/>
			<div className='text-center text-sm'>
				<Link to='/login' className='text-muted-foreground underline-offset-4 hover:underline'>
					{t('auth.reset_password.back_to_login')}
				</Link>
			</div>
		</AuthShell>
	)
}
